import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  EventqDialogPopover,
  EventqDialogPopoverPlacement,
} from '../dialog-popover/dialog-popover';
import {
  EventqSearchableList,
  type EventqSearchableListColumn,
  type EventqSearchableListRow,
} from '../searchable-list/searchable-list';
import type { SitesStatusListSite, SitesStatusListVariant } from './sites-status-list-popover.types';

export type { SitesStatusListSite, SitesStatusListVariant };

const SITE_COLUMNS: EventqSearchableListColumn[] = [
  { key: 'name', style: 'primary' },
  { key: 'address', style: 'secondary' },
];

@Component({
  selector: 'eventq-sites-status-list-popover',
  imports: [EventqDialogPopover, EventqSearchableList],
  templateUrl: './sites-status-list-popover.html',
  styleUrl: './sites-status-list-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqSitesStatusListPopover {
  readonly open = input(false);
  readonly variant = input.required<SitesStatusListVariant>();
  readonly sites = input.required<SitesStatusListSite[]>();
  readonly searchQuery = input('');
  readonly searchPlaceholder = input('Search');
  readonly closeLabel = input('Close');
  /** When false, rows are view-only (no siteSelect). */
  readonly allowSiteSelect = input(true);
  /** When true, forwards to the inner searchable list skeleton. */
  readonly loading = input(false);
  /** Skeleton row count while loading. Defaults to the searchable-list default. */
  readonly skeletonCount = input<number | null>(null);
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement | 'auto'>('auto');
  readonly panelWidth = input<string | null>('420px');
  /** Caps panel height; panel still shrinks with few sites. */
  readonly panelMaxHeight = input<string | null>('560px');

  readonly closed = output<void>();
  readonly siteSelect = output<SitesStatusListSite>();
  readonly searchQueryChange = output<string>();

  readonly siteColumns = SITE_COLUMNS;

  readonly siteRows = computed<EventqSearchableListRow[]>(() =>
    this.sites().map((site) => ({
      id: site.id,
      name: site.name,
      address: site.address,
    })),
  );

  readonly title = computed(() =>
    this.variant() === 'armed' ? 'Armed Sites' : 'Disarmed Sites',
  );

  readonly titleIcon = computed(() =>
    this.variant() === 'armed' ? 'verified_user' : 'security',
  );

  readonly resolvedPlacement = computed((): EventqDialogPopoverPlacement => {
    const placement = this.placement();
    if (placement !== 'auto') {
      return placement;
    }

    return this.anchor() ? 'anchor-top-end' : 'center';
  });

  onSiteSelect(siteId: string): void {
    if (!this.allowSiteSelect()) {
      return;
    }

    const site = this.sites().find((entry) => entry.id === siteId);
    if (!site) {
      return;
    }

    this.siteSelect.emit(site);
  }
}
